import moment, { Moment } from "moment";
import { Component, Inject, Injector, OnInit } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { GenericComponent } from "@shared/components/generic.component";
import { BudgetService } from "@core/services/budget.service";
import { BudgetDetails } from "@shared/models/service-request-details-dto";
import { PeriodDTO } from "@shared/models/period-dto";
import { ServiceTypeEnum } from "@shared/enums/service-type-enum";
import { AbstractControl, Validators } from "@angular/forms";
import { ServiceRequestService } from "@core/services/service-request.service";
import { DateAdapter } from "@angular/material/core";
import { LabelService, defaultMileageUnit } from "@core/services/label.service";

interface PropsType {
  budgetDetails?: BudgetDetails;
  currentKm: number;
  serviceType: string;
  editCompletionPredictionOnly?: boolean;
  completionPrediction?: Date | string;
  completionPredictionPeriod?: string;
  carWorkshopKm?: number;
  vehicleArrivalConfirmationDateByUser?: Date | string;
}

@Component({
  templateUrl: "./modal-confirm-vehicle-arrival.component.html",
  styleUrls: ["./modal-confirm-vehicle-arrival.component.scss"],
  standalone: false
})
export class ModalConfirmVehicleArrivalComponent
  extends GenericComponent
  implements OnInit
{
  form = this.fb.group({
    date: [""],
    arrivalTime: ["", Validators.required],
    arrivalDate: ["", Validators.required],
    period: [""],
    carWorkshopKm: ["", Validators.required],
    vehicleArrivalConfirmationDateByUser: [""]
  });

  periods: PeriodDTO[] = [];
  canShowWarning = false;
  currentKm: number;
  serviceType: string;
  isCompletionPredictionRequired = false;
  canShowSameKmWarning = false;
  isEditCompletionPredictionOnly = false;

  mileageUnit = defaultMileageUnit;

  constructor(
    injector: Injector,
    @Inject(MAT_DIALOG_DATA) public data: PropsType,
    private budgetService: BudgetService,
    private serviceRequestService: ServiceRequestService,
    private matDialogRef: MatDialogRef<ModalConfirmVehicleArrivalComponent>,
    private dateAdapter: DateAdapter<any>,
    private labelService: LabelService
  ) {
    super(injector);
    this.currentKm = data.currentKm;
    this.serviceType = data.serviceType;
  }

  ngOnInit(): void {
    this.mileageUnit = this.labelService.getMileageUnit();
    this.isEditCompletionPredictionOnly =
      this.data?.editCompletionPredictionOnly === true;
    this.isCompletionPredictionRequired = this.isPreventiveServiceType();
    this.setCompletionPredictionValidators();
    this.listAllPeriod();
    if (this.data?.budgetDetails) {
      this.canShowWarningBudget(this.data.budgetDetails);
    }
    this.setInitialValues();
    this.dateAdapter.setLocale(this.translateService.currentLang.toLowerCase());
  }

  private setInitialValues(): void {
    const vehicleArrivalConfirmationDate = this.getMomentOrNull(
      this.data?.vehicleArrivalConfirmationDateByUser
    );
    const completionPrediction = this.getMomentOrNull(
      this.data?.completionPrediction
    );
    const carWorkshopKm = this.isEditCompletionPredictionOnly
      ? this.data?.carWorkshopKm ?? this.data?.currentKm ?? ""
      : this.data?.carWorkshopKm ?? "";

    this.form.patchValue({
      arrivalDate: vehicleArrivalConfirmationDate,
      arrivalTime: vehicleArrivalConfirmationDate
        ? this.getTimeValue(vehicleArrivalConfirmationDate)
        : "",
      date: completionPrediction,
      period: this.data?.completionPredictionPeriod || "",
      carWorkshopKm
    });

    if (!this.isEditCompletionPredictionOnly) {
      return;
    }
  }

  private disableControlIfHasValue(controlName: string, value: unknown): void {
    if (value === null || value === undefined || value === "") {
      return;
    }

    this.form.get(controlName)?.disable();
  }

  private setCompletionPredictionValidators(): void {
    const validators = this.isPreventiveServiceType()
      ? [Validators.required]
      : [];

    this.form.get("date")?.setValidators(validators);
    this.form.get("date")?.updateValueAndValidity();
    this.form.get("period")?.setValidators(validators);
    this.form.get("period")?.updateValueAndValidity();
  }

  private isPreventiveServiceType(): boolean {
    return this.serviceType === ServiceTypeEnum.PREVENTIVE;
  }

  private listAllPeriod(): void {
    this.serviceRequestService.listAllTypePeriod().subscribe(
      (periods) => {
        this.periods = periods;
      },
      (error) => {
        this.showErrorMessage(error, "Error to list periods");
      }
    );
  }

  private parseDateSafe(value: string): Date {
    const date = new Date(value);
    const offsetMs = date.getTimezoneOffset() * 60 * 1000;
    return new Date(date.getTime() + offsetMs);
  }

  private canShowWarningBudget(budget: BudgetDetails): void {
    this.budgetService.hasVerificationItems(budget.id).subscribe(
      (data) => {
        this.canShowWarning = data;
      },
      (error) => {
        this.showErrorMessage(error, "Error to find Budget");
      }
    );
  }

  private formatDateTime(date: Date, time?: string): string {
    const pad = (n: number) => (n < 10 ? "0" + n : n);

    if (time) {
      const [hours, minutes] = time.split(":").map(Number);
      date.setHours(hours, minutes, 0, 0);
    }

    return (
      date.getFullYear() +
      "-" +
      pad(date.getMonth() + 1) +
      "-" +
      pad(date.getDate()) +
      "T" +
      pad(date.getHours()) +
      ":" +
      pad(date.getMinutes()) +
      ":" +
      pad(date.getSeconds())
    );
  }

  private getMomentOrNull(value?: Date | string): Moment | null {
    if (!value) {
      return null;
    }

    const parsedDate = moment(value);
    return parsedDate.isValid() ? parsedDate : null;
  }

  private getDateValue(value: Date | Moment | string): Date {
    if (moment.isMoment(value)) {
      return value.toDate();
    }

    return new Date(value);
  }

  private getTimeValue(date: Moment): string {
    const hours = String(date.hours()).padStart(2, "0");
    const minutes = String(date.minutes()).padStart(2, "0");

    return `${hours}:${minutes}`;
  }

  private getVehicleArrivalConfirmationDateValue(formatted: string): string {
    if (!this.isEditCompletionPredictionOnly) {
      return formatted;
    }

    if (typeof this.data?.vehicleArrivalConfirmationDateByUser === "string") {
      return this.data.vehicleArrivalConfirmationDateByUser;
    }

    const vehicleArrivalConfirmationDate = this.getMomentOrNull(
      this.data?.vehicleArrivalConfirmationDateByUser
    );

    return vehicleArrivalConfirmationDate
      ? this.formatDateTime(vehicleArrivalConfirmationDate.toDate())
      : formatted;
  }

  closeDialog(isSubmitted: boolean): void {
    if (isSubmitted && this.validaDate()) {
      const arrivalDate = this.getDateValue(
        this.form.get("arrivalDate")?.value
      );
      const arrivalTime = this.form.get("arrivalTime")?.value;
      const completionPredictionDate = this.form.get("date")?.value;

      const formatted = this.formatDateTime(arrivalDate, arrivalTime);

      this.form
        .get("vehicleArrivalConfirmationDateByUser")
        ?.setValue(this.getVehicleArrivalConfirmationDateValue(formatted));

      const payload = {
        ...this.form.getRawValue(),
        arrivalDate: this.formatDateTime(
          this.parseDateSafe(this.form.get("arrivalDate")?.value)
        ),
        date: completionPredictionDate
          ? this.formatDateTime(this.parseDateSafe(completionPredictionDate))
          : null,
        vehicleArrivalConfirmationDateByUser: this.form.get(
          "vehicleArrivalConfirmationDateByUser"
        )?.value
      };

      return this.matDialogRef.close(payload);
    }

    this.matDialogRef.close(null);
  }

  getOnlyNumbersMask() {
    return this.maskService.getOnlyNumbersMask();
  }

  validaDate(): boolean {
    const inputDate = this.form.get("arrivalDate")?.value;
    const inputTime = this.form.get("arrivalTime")?.value;
    const today = new Date();

    if (!inputDate || !inputTime) return false;

    const dateObj = this.getDateValue(inputDate);
    const [hours, minutes] = inputTime.split(":").map(Number);

    dateObj.setHours(hours, minutes, 0, 0);

    if (dateObj > today) {
      this.form.get("arrivalDate")?.setErrors({
        isGreaterThanCurrentDate: true
      });
      return false;
    } else {
      this.form.get("arrivalDate")?.setErrors(null);
      return true;
    }
  }

  checkMinValue(): void {
    const carWorkshopKmControl = this.form.get("carWorkshopKm");
    const value = Number(carWorkshopKmControl?.value);

    if (!carWorkshopKmControl || !carWorkshopKmControl.value) {
      this.canShowSameKmWarning = false;
      this.removeCarWorkshopKmError(carWorkshopKmControl);
      return;
    }

    if (Number.isNaN(value)) {
      this.canShowSameKmWarning = false;
      return;
    }

    if (value === 0 || value < this.currentKm) {
      this.canShowSameKmWarning = false;
      this.setCarWorkshopKmError(carWorkshopKmControl);
      return;
    }

    this.removeCarWorkshopKmError(carWorkshopKmControl);
    this.canShowSameKmWarning = value === this.currentKm;
  }

  private setCarWorkshopKmError(carWorkshopKmControl: AbstractControl): void {
    carWorkshopKmControl.setErrors({
      ...(carWorkshopKmControl.errors ?? {}),
      isGreatherThanCurrentKm: true
    });
  }

  private removeCarWorkshopKmError(
    carWorkshopKmControl: AbstractControl | null
  ): void {
    if (!carWorkshopKmControl?.errors?.isGreatherThanCurrentKm) {
      return;
    }

    const errors = { ...(carWorkshopKmControl.errors ?? {}) };
    delete errors.isGreatherThanCurrentKm;
    carWorkshopKmControl.setErrors(Object.keys(errors).length ? errors : null);
  }

  private combineDateAndTime(): string {
    const date = this.form.get("arrivalDate")?.value;
    const time = this.form.get("arrivalTime")?.value;

    return `${date}T${time}:00`;
  }
}
